import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MessageDraft } from "~/api/conversationTypes";
import { useApi } from "~/api/provider";
import { queryKeys } from "~/api/queries";
import {
  initialStreamState,
  reduceStream,
  type StreamState,
} from "~/features/conversations/transcript/streamState";

/**
 * One turn, streamed (W8 §B.3).
 *
 * Three things kms did not do, all of them here:
 *  - the stream is **aborted** when the drawer unmounts or the conversation changes. kms had
 *    no `AbortSignal`, so an abandoned turn kept reading until the tab closed.
 *  - live state is **reset per conversation** (W8-12) — kms leaked `hiddenMessageIds` and
 *    skill status from one conversation into the next.
 *  - the conversation is **re-read when the turn completes**, so the synthetic rows retire
 *    against the real messages instead of doubling them.
 */
export function useConversationStream(conversationId: string | null) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [state, setState] = useState<StreamState>(initialStreamState);
  const abortRef = useRef<AbortController | null>(null);

  // A new conversation is a new stream, and the old one has nothing to say about it. The id
  // is read INSIDE the effect so it is a real dependency rather than a mutation of a ref.
  useEffect(() => {
    void conversationId;
    abortRef.current?.abort();
    abortRef.current = null;
    setState(initialStreamState());
  }, [conversationId]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((current) => ({ ...current, running: false }));
  }, []);

  const send = useCallback(
    async (draft: MessageDraft): Promise<void> => {
      if (!conversationId) {
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setState({ ...initialStreamState(), running: true });

      try {
        await api.conversations.stream(
          conversationId,
          draft,
          (event) => {
            setState((current) => reduceStream(current, event));
          },
          controller.signal,
        );
      } catch (error) {
        // An `error` FRAME inside a 200 lands here as a rejection (W8-21) — and so does a
        // genuine transport failure. Both are the same thing to a reader: the answer stopped.
        setState((current) => ({
          ...current,
          running: false,
          error: error instanceof Error ? error.message : "The assistant could not answer",
        }));
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        void queryClient.invalidateQueries({
          queryKey: queryKeys.conversationDetail(conversationId),
        });
      }
    },
    [api, conversationId, queryClient],
  );

  return { state, send, stop };
}
